const DEFAULT_RETURN_TO = "/jobs";
const ALLOWED_RETURN_PATHS = new Set(["/jobs", "/jobs/my-jobs", "/cv"]);
// A delegated editor works under /cv/access/<grantId>. Without this the
// allowlist rejected their own return path and dropped them onto /cv: their
// OWN CV instead of the shared one (issue #246). Single segment only, so it
// can't be widened into an open redirect.
const DELEGATED_CV_RETURN_PATH = /^\/cv\/access\/[^/]+$/;

type RouteParam = string | string[] | null | undefined;

function firstRouteParam(value: RouteParam) {
  return Array.isArray(value) ? value[0] : value;
}

export function normalizeJobPreferencesReturnTo(value: RouteParam) {
  const rawParam = firstRouteParam(value)?.trim();
  const raw = rawParam
    ? (() => {
        try {
          return decodeURIComponent(rawParam);
        } catch {
          return rawParam;
        }
      })()
    : undefined;
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return null;

  const [withoutHash] = raw.split("#");
  const [path, query] = withoutHash.split("?");
  if (!ALLOWED_RETURN_PATHS.has(path) && !DELEGATED_CV_RETURN_PATH.test(path))
    return null;

  return query ? `${path}?${query}` : path;
}

export function buildJobPreferencesRoute(returnTo: string = DEFAULT_RETURN_TO) {
  const safeReturnTo =
    normalizeJobPreferencesReturnTo(returnTo) ?? DEFAULT_RETURN_TO;

  return `/cv/edit-job-preferences?returnTo=${encodeURIComponent(safeReturnTo)}` as const;
}

/**
 * Native entry point for the same editor, hosted on the root stack
 * (`app/job-preferences.tsx`).
 *
 * `/cv/edit-job-preferences` lives inside the CV drawer scene, so pushing it
 * from Jobs switched the drawer over to the CV scene: the My Tabbio hub (or its
 * skeleton) flashed behind the sheet on the way in, and dismissing the sheet
 * without saving stranded the user on the hub instead of back on Jobs. The root
 * route presents over whatever screen asked for it. Web keeps the CV route,
 * where a full-page navigation with browser Back is the right behaviour.
 */
export function buildRootJobPreferencesRoute(returnTo: string = DEFAULT_RETURN_TO) {
  const safeReturnTo =
    normalizeJobPreferencesReturnTo(returnTo) ?? DEFAULT_RETURN_TO;

  return `/job-preferences?returnTo=${encodeURIComponent(safeReturnTo)}` as const;
}
